import {Trans} from '@lingui/macro';
import {chain} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useMemo} from 'react';

import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormMultiselectAssigneesProps extends LensFormMultiselectProps<TeammateModel> {}

/*
 * Component.
 */

export const LensFormMultiSelectAssignees: FC<LensFormMultiselectAssigneesProps> = (props) => {
  const {isDisabled, choices, selectedIds, onSetItemIds} = props;

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(choices);
    return computeFilterDropdownSourceAsync(fetcher);
  }, [choices]);

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-assignees-placeholder">Select assignees</Trans>
      )}
      label={<Trans id="lens-form-multiselect-assignees-label">With at least one of these assignees</Trans>}
      dropdownTitle={<Trans id="lens-form-multiselect-assignees-dropdown-title">All teammates</Trans>}
      isDisabled={isDisabled}
      isRequired={false}
    />
  );
};

/*
 * Helpers.
 */

const filterRowsByQuery = memoize(20)((teammates: ReadonlyArray<TeammateModel>, searchQuery: string) => {
  // For empty query, render all choices.
  if (!searchQuery) {
    return teammates;
  }

  // Otherwise, filter by name.
  const lowerCaseQuery = searchQuery.toLowerCase();
  return (
    chain(teammates)
      // TODO-LENS: consider using localeCompare method instead. Based on makeFilterRows<T> which compare like this does.
      .filter((teammate) => buildTeammateDisplayName(teammate).toLowerCase().includes(lowerCaseQuery))
      .value()
  );
});

function renderValue(props: LensFormMultiselectAssigneesProps) {
  const {selectedIds, choices} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTeammates = chain(selectedIds)
    .map((id) => choices.find((teammate) => teammate.id === id))
    .compact()
    .value();

  return <>{selectedTeammates.map((teammate) => buildTeammateDisplayName(teammate)).join(', ')}</>;
}

const buildRowGenerator: FilterRowGenerator<TeammateModel> = (
  teammate,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isSelected = dropdownSelectedIds.includes(teammate.id);

  return {
    isSelected,
    renderer: () => (
      <DropdownItemContent isChecked={isSelected}>{buildTeammateDisplayName(teammate)}</DropdownItemContent>
    ),
    onSelect: makeOnItemSelect(teammate.id),
  };
};

function buildFetcher(choices: ReadonlyArray<TeammateModel>): DropdownFetcher<TeammateModel> {
  return async (query) => ({
    items: filterRowsByQuery(choices, query),
    canLoadMore: false,
  });
}
